import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Descriptions, Tag, Button, Space, Typography, Table, Spin, message, Avatar, Modal, Form, Input, Switch, Select } from 'antd';
import { ArrowLeftOutlined, UserOutlined, MailOutlined, SafetyOutlined, DeleteOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import ReactCountryFlag from 'react-country-flag';

const { Title } = Typography;

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  accentDim: 'rgba(34, 211, 238, 0.1)',
  hover: '#1f2a37',
};

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  
  .ant-btn {
    background: ${COLORS.bgSecondary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
    height: 40px;
    display: flex;
    align-items: center;
    gap: 8px;
    
    &:hover {
      border-color: ${COLORS.accent} !important;
      color: ${COLORS.accent} !important;
      background: ${COLORS.hover} !important;
    }
  }
`;

const StyledCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 24px;
  
  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-card-head-title {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-card-body {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-descriptions-item-label {
    background: ${COLORS.bgPrimary} !important;
    color: ${COLORS.textSecondary} !important;
    border-color: ${COLORS.border} !important;
  }
  
  .ant-descriptions-item-content {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
    border-color: ${COLORS.border} !important;
  }
  
  .ant-table {
    background: transparent !important;
  }
  
  .ant-table-thead > tr > th {
    background: ${COLORS.bgPrimary} !important;
    color: ${COLORS.textSecondary} !important;
    border-color: ${COLORS.border} !important;
  }
  
  .ant-table-tbody > tr > td {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
    border-color: ${COLORS.border} !important;
  }
  
  .ant-table-tbody > tr:hover > td {
    background: ${COLORS.hover} !important;
  }
`;

const UserDetails: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const userType = searchParams.get('userType') || 'Administrator';
  
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState<any>(null);
  const [sendEmailModalVisible, setSendEmailModalVisible] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendForm] = Form.useForm();
  const [roleModalVisible, setRoleModalVisible] = useState(false);
  const [roleForm] = Form.useForm();
  const [roleOptions, setRoleOptions] = useState<string[]>([]);
  const [permissionOptions, setPermissionOptions] = useState<string[]>([]);
  const [submittingRole, setSubmittingRole] = useState(false);
  const [resetUser2FAModalVisible, setResetUser2FAModalVisible] = useState(false);
  const [resettingMerchant2FA, setResettingMerchant2FA] = useState(false);
  const [resetUser2FAForm] = Form.useForm();
  const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetPasswordForm] = Form.useForm();
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [deleteForm] = Form.useForm();

  useEffect(() => {
    fetchUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, userType]);

  useEffect(() => {
    if (!roleModalVisible) return;
    // fetch enums for roles/permissions
    (async () => {
      try {
        const resp = await apiClient.get(API_ENDPOINTS.PUBLIC_GET_ENUMS);
        const data = resp.data?.data || {};
        if (userType === 'Administrator') {
          setRoleOptions(data.administratorRoles || []);
          setPermissionOptions(data.administratorPermissions || []);
        } else {
          setRoleOptions(data.merchantRoles || []);
          setPermissionOptions(data.merchantPermissions || []);
        }
      } catch (err) {
        // ignore, fallback to defaults
      }
    })();
  }, [roleModalVisible, userType]);

  const fetchUserDetails = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      const response = await apiClient.get(
        (API_ENDPOINTS.ADMIN_GET_USER_DETAILS || '/api/Administrator/user/{userId}/{userType}').replace('{userId}', userId).replace('{userType}', userType)
      );
      
      if (response.data.status === 'success') {
        setUserData(response.data.data);
      } else {
        message.error('Failed to load user details');
      }
    } catch (error: any) {
      console.error('Error fetching user details:', error);
      message.error(error.response?.data?.message || 'Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusTag = (status: string) => {
    const statusConfig: Record<string, { color: string; text: string }> = {
      ACTIVE: { color: '#10B981', text: 'Active' },
      PENDING: { color: '#F59E0B', text: 'Pending' },
      SUSPENDED: { color: '#EF4444', text: 'Suspended' },
      DELETED: { color: '#6B7280', text: 'Deleted' },
    };
    const config = statusConfig[status] || { color: '#6B7280', text: status };
    return (
      <Tag 
        color="transparent" 
        style={{ 
          color: config.color, 
          border: `1px solid ${config.color}40`,
          background: `${config.color}10`,
          borderRadius: '6px',
          padding: '0 8px'
        }}
      >
        {config.text}
      </Tag>
    );
  };

  const loginHistoryColumns = [
    {
      title: 'Login Time',
      dataIndex: 'loginTime',
      key: 'loginTime',
      render: (date: string) => date ? new Date(date).toLocaleString() : '-',
    },
    {
      title: 'IP Address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
    },
    {
      title: 'Device Info',
      dataIndex: 'deviceInfo',
      key: 'deviceInfo',
      render: (info: any) => {
        if (!info) return '-';
        const parts = [];
        if (info.browser) parts.push(info.browser);
        if (info.os) parts.push(info.os);
        if (info.device) parts.push(info.device);
        return parts.length > 0 ? parts.join(' / ') : JSON.stringify(info);
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusTag(status),
    },
  ];

  if (loading) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spin size="large" />
        </div>
      </PageContainer>
    );
  }

  if (!userData) {
    return (
      <PageContainer>
        <HeaderContainer>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Back
          </Button>
          <Title level={2} style={{ margin: 0, color: COLORS.textPrimary }}>
            User Not Found
          </Title>
        </HeaderContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeaderContainer>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Title level={2} style={{ margin: 0, color: COLORS.textPrimary }}>
          {userType} Details
        </Title>
        <Button
          type="primary"
          icon={<MailOutlined />}
          onClick={() => setSendEmailModalVisible(true)}
        >
          Send Email
        </Button>
        <Button
          style={{ marginLeft: 8 }}
          onClick={() => setRoleModalVisible(true)}
        >
          Change Role
        </Button>
        {(userType === 'Merchant' || userType === 'Administrator') && (
          <Button
            danger
            icon={<SafetyOutlined />}
            disabled={!userData.twoFactorEnabled}
            onClick={() => setResetUser2FAModalVisible(true)}
          >
            Reset {userType} 2FA
          </Button>
        )}
        {(userType === 'Merchant' || userType === 'Administrator') && (
          <Button
            danger
            icon={<SafetyOutlined />}
            onClick={() => { setTempPassword(null); setResetPasswordModalVisible(true); }}
          >
            Reset {userType} Password
          </Button>
        )}
        {(userType === 'Merchant' || userType === 'Administrator') && (
          <Button
            danger
            type="primary"
            icon={<DeleteOutlined />}
            disabled={userData.status === 'DELETED'}
            onClick={() => setDeleteModalVisible(true)}
          >
            {userData.status === 'DELETED' ? `${userType} Deleted` : `Delete ${userType}`}
          </Button>
        )}
      </HeaderContainer>

      <StyledCard
        title={
          <Space>
            <Avatar
              src={userData.portraitUrl ? `data:image/jpeg;base64,${userData.portraitUrl}` : undefined}
              icon={<UserOutlined />}
              size={64}
              style={{ backgroundColor: COLORS.bgSecondary, color: COLORS.textPrimary, border: `1px solid ${COLORS.border}` }}
            />
            <div>
              <div style={{ color: COLORS.textPrimary, fontSize: '18px', fontWeight: 600 }}>
                {userData.firstName} {userData.lastName}
              </div>
              <div style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
                {userData.email}
              </div>
            </div>
          </Space>
        }
      >
        <Descriptions column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} bordered>
          <Descriptions.Item label="User ID">{userData.id}</Descriptions.Item>
          <Descriptions.Item label="Email">{userData.email}</Descriptions.Item>
          <Descriptions.Item label="First Name">{userData.firstName}</Descriptions.Item>
          <Descriptions.Item label="Last Name">{userData.lastName}</Descriptions.Item>
          <Descriptions.Item label="Phone Number">{userData.phoneNumber || '-'}</Descriptions.Item>
          <Descriptions.Item label="Country">
            <Space>
              {userData.countryCode && (
                <ReactCountryFlag
                  countryCode={userData.countryCode}
                  svg
                  style={{
                    width: '1.5em',
                    height: '1.5em',
                    borderRadius: '2px'
                  }}
                />
              )}
              <span>{userData.countryCode || '-'}</span>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Status">{getStatusTag(userData.status)}</Descriptions.Item>
          <Descriptions.Item label="2FA Enabled">
            {userData.twoFactorEnabled ? (
              <Tag color="green">Enabled</Tag>
            ) : (
              <Tag color="default">Disabled</Tag>
            )}
          </Descriptions.Item>
          {userData.role && (
            <Descriptions.Item label="Role">
              <Tag color="blue">{userData.role}</Tag>
            </Descriptions.Item>
          )}
          {userData.permissions && userData.permissions.length > 0 && (
            <Descriptions.Item label="Permissions">
              <Space wrap>
                {userData.permissions.map((perm: string, index: number) => (
                  <Tag key={index} color="cyan">{perm}</Tag>
                ))}
              </Space>
            </Descriptions.Item>
          )}
          {userData.merchantCompanyId && (
            <Descriptions.Item label="Merchant Company ID">{userData.merchantCompanyId}</Descriptions.Item>
          )}
          {userData.company && (
            <>
              <Descriptions.Item label="Company Name">{userData.company.name || '-'}</Descriptions.Item>
              <Descriptions.Item label="Company Email">{userData.company.companyEmail || '-'}</Descriptions.Item>
            </>
          )}
          <Descriptions.Item label="Created At">
            {userData.createdAt ? new Date(userData.createdAt).toLocaleString() : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            {userData.updatedAt ? new Date(userData.updatedAt).toLocaleString() : '-'}
          </Descriptions.Item>
        </Descriptions>
      </StyledCard>

      {userData.loginHistory && userData.loginHistory.length > 0 && (
        <StyledCard title="Recent Login History">
          <Table
            columns={loginHistoryColumns}
            dataSource={userData.loginHistory}
            rowKey={(record, index) => record.id || index?.toString() || ''}
            pagination={false}
            style={{ background: 'transparent' }}
          />
        </StyledCard>
      )}

      {/* Send Email Modal */}
      <Modal
        title="Send Email"
        open={sendEmailModalVisible}
        onCancel={() => {
          setSendEmailModalVisible(false);
          sendForm.resetFields();
        }}
        footer={null}
        width={600}
        style={{ top: 20 }}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
      >
        <Form
          form={sendForm}
          layout="vertical"
          onFinish={async (values) => {
            setSending(true);
            try {
              await apiClient.post(API_ENDPOINTS.ADMIN_SEND_INTERNAL_EMAIL, {
                recipientEmail: userData.email,
                recipientUserId: userData.id,
                recipientUserType: userType,
                subject: values.subject,
                message: values.message,
                createCriticalNotification: values.createCriticalNotification !== false,
              });
              message.success('Email sent successfully');
              setSendEmailModalVisible(false);
              sendForm.resetFields();
            } catch (error: any) {
              message.error(error.response?.data?.message || 'Failed to send email');
            } finally {
              setSending(false);
            }
          }}
        >
          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: 'Please enter subject' }]}
          >
            <Input placeholder="Email subject" />
          </Form.Item>
          <Form.Item
            name="message"
            label="Message"
            rules={[{ required: true, message: 'Please enter message' }]}
          >
            <Input.TextArea rows={6} placeholder="Email message content" />
          </Form.Item>
          <Form.Item
            name="createCriticalNotification"
            label="Create Critical Notification"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={sending}>
                Send Email
              </Button>
              <Button onClick={() => {
                setSendEmailModalVisible(false);
                sendForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Change Role Modal */}
      <Modal
        title={`Change ${userType} Role`}
        open={roleModalVisible}
        onCancel={() => {
          setRoleModalVisible(false);
          roleForm.resetFields();
        }}
        footer={null}
        width={600}
        style={{ top: 20 }}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
      >
        <Form
          form={roleForm}
          layout="vertical"
          initialValues={{ role: userData.role, permissions: userData.permissions || [] }}
          onFinish={async (values) => {
            setSubmittingRole(true);
            try {
              const endpoint = userType === 'Administrator'
                ? (API_ENDPOINTS.ADMIN_UPDATE_ADMINISTRATOR_ROLE || '/api/Administrator/user/{userId}/role').replace('{userId}', userId || '')
                : (API_ENDPOINTS.MERCHANT_UPDATE_MERCHANT_ROLE || '/api/Merchant/lobby/company/staff/{userId}/role').replace('{userId}', userId || '');

              await apiClient.put(endpoint, {
                role: values.role,
                permissions: values.permissions || [],
              });

              message.success('Role updated successfully');
              setRoleModalVisible(false);
              roleForm.resetFields();
              fetchUserDetails();
            } catch (error: any) {
              message.error(error.response?.data?.message || 'Failed to update role');
            } finally {
              setSubmittingRole(false);
            }
          }}
        >
          <Form.Item name="role" label="Role" rules={[{ required: true, message: 'Please select a role' }]}>
            <Select className="select-with-text" placeholder="Select role">
              {(roleOptions.length ? roleOptions : (userType === 'Administrator' ? ['SUPER_ADMIN','ADMIN','COMPLIANCE_OFFICER'] : ['SUPER_MERCHANT','MERCHANT'])).map((r) => (
                <Select.Option key={r} value={r}>{r}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="permissions" label="Permissions">
            <Select className="select-with-text" mode="multiple" placeholder="Select permissions">
              {(permissionOptions.length ? permissionOptions : []).map((p) => (
                <Select.Option key={p} value={p}>{p}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submittingRole}>
                Update Role
              </Button>
              <Button onClick={() => { setRoleModalVisible(false); roleForm.resetFields(); }}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Reset ${userType} 2FA`}
        open={resetUser2FAModalVisible}
        onCancel={() => {
          setResetUser2FAModalVisible(false);
          resetUser2FAForm.resetFields();
        }}
        footer={null}
        width={520}
        style={{ top: 20 }}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
      >
        <div style={{ color: COLORS.textSecondary, marginBottom: 16, lineHeight: 1.6 }}>
          This will clear the user&apos;s current 2FA secret, revoke active sessions, and send an email telling them to set up 2FA again on their next login.
        </div>

        <Form
          form={resetUser2FAForm}
          layout="vertical"
          onFinish={async (values) => {
            if (!userId) return;

            setResettingMerchant2FA(true);
            try {
              const endpoint = (API_ENDPOINTS.ADMIN_RESET_USER_2FA || '/api/Administrator/user/{userId}/{userType}/reset-2fa')
                .replace('{userId}', userId)
                .replace('{userType}', userType);

              const response = await apiClient.post(endpoint, {
                twoFaCode: values.twoFaCode,
              });

              message.success(response.data?.message || `${userType} 2FA reset successfully`);
              setResetUser2FAModalVisible(false);
              resetUser2FAForm.resetFields();
              setUserData((current: any) => current ? {
                ...current,
                twoFactorEnabled: false,
              } : current);
            } catch (error: any) {
              message.error(error.response?.data?.message || `Failed to reset ${userType.toLowerCase()} 2FA`);
            } finally {
              setResettingMerchant2FA(false);
            }
          }}
        >
          <Form.Item
            name="twoFaCode"
            label="Your Current Admin 2FA Code"
            rules={[
              { required: true, message: 'Please enter your current 2FA code' },
              { pattern: /^\d{6}$/, message: 'Please enter a valid 6-digit code' },
            ]}
          >
            <Input
              maxLength={6}
              placeholder="Enter your 6-digit 2FA code"
              prefix={<SafetyOutlined />}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button danger type="primary" htmlType="submit" loading={resettingMerchant2FA}>
                Reset {userType} 2FA
              </Button>
              <Button onClick={() => {
                setResetUser2FAModalVisible(false);
                resetUser2FAForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Reset password (SUPER_ADMIN) */}
      <Modal
        title={`Reset ${userType} Password`}
        open={resetPasswordModalVisible}
        onCancel={() => {
          setResetPasswordModalVisible(false);
          resetPasswordForm.resetFields();
          setTempPassword(null);
        }}
        footer={null}
      >
        {tempPassword ? (
          <div>
            <p style={{ marginBottom: 8 }}>
              Temporary password generated. Share it with the user over a secure channel — it is shown only once.
              The user has also been emailed and should change it on next login.
            </p>
            <Input.Password
              readOnly
              value={tempPassword}
              visibilityToggle
              style={{ fontFamily: 'monospace' }}
              addonAfter={
                <a onClick={() => { void navigator.clipboard?.writeText(tempPassword); message.success('Copied'); }}>Copy</a>
              }
            />
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <Button
                type="primary"
                onClick={() => {
                  setResetPasswordModalVisible(false);
                  resetPasswordForm.resetFields();
                  setTempPassword(null);
                }}
              >
                Done
              </Button>
            </div>
          </div>
        ) : (
          <Form
            layout="vertical"
            form={resetPasswordForm}
            onFinish={async (values) => {
              if (!userId) return;
              setResettingPassword(true);
              try {
                const endpoint = (API_ENDPOINTS.ADMIN_RESET_USER_PASSWORD || '/api/Administrator/user/{userId}/{userType}/reset-password')
                  .replace('{userId}', userId)
                  .replace('{userType}', userType);
                const response = await apiClient.post(endpoint, { twoFaCode: values.twoFaCode });
                const temp = response.data?.data?.temporaryPassword;
                if (temp) {
                  setTempPassword(temp);
                  message.success(`${userType} password reset`);
                } else {
                  message.success(response.data?.message || `${userType} password reset`);
                  setResetPasswordModalVisible(false);
                }
                resetPasswordForm.resetFields();
              } catch (error: any) {
                message.error(error.response?.data?.message || `Failed to reset ${userType.toLowerCase()} password`);
              } finally {
                setResettingPassword(false);
              }
            }}
          >
            <p style={{ color: '#8c8c8c', marginBottom: 12 }}>
              This sets a new temporary password, revokes the user&apos;s active sessions, and emails them a notice.
              Requires your current 2FA code.
            </p>
            <Form.Item
              name="twoFaCode"
              label="Your Current Admin 2FA Code"
              rules={[
                { required: true, message: 'Please enter your current 2FA code' },
                { pattern: /^\d{6}$/, message: 'Please enter a valid 6-digit code' },
              ]}
            >
              <Input maxLength={6} placeholder="Enter your 6-digit 2FA code" prefix={<SafetyOutlined />} />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button danger type="primary" htmlType="submit" loading={resettingPassword}>
                  Reset {userType} Password
                </Button>
                <Button onClick={() => {
                  setResetPasswordModalVisible(false);
                  resetPasswordForm.resetFields();
                }}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>
      {/* Delete user (SUPER_ADMIN) */}
      <Modal
        title={`Delete ${userType}`}
        open={deleteModalVisible}
        onCancel={() => {
          setDeleteModalVisible(false);
          deleteForm.resetFields();
        }}
        footer={null}
        width={520}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
      >
        <div style={{ color: COLORS.textSecondary, marginBottom: 16, lineHeight: 1.6 }}>
          This sets <strong style={{ color: COLORS.textPrimary }}>{userData.firstName} {userData.lastName}</strong>
          {' '}(<span style={{ color: COLORS.textPrimary }}>{userData.email}</span>) to <strong style={{ color: COLORS.textPrimary }}>deleted</strong>.
          They will no longer be able to sign in, and any session they currently hold is revoked immediately.
          Their record and history are kept — this is not a permanent erase, but it cannot be undone from this screen.
        </div>

        <Form
          form={deleteForm}
          layout="vertical"
          onFinish={async (values) => {
            if (!userId) return;

            setDeletingUser(true);
            try {
              const endpoint = (API_ENDPOINTS.ADMIN_DELETE_USER || '/api/Administrator/user/delete/{userId}/{userType}')
                .replace('{userId}', userId)
                .replace('{userType}', userType);

              // The body carries the 2FA code, so this has to be axios' `data` option rather
              // than a second positional argument - DELETE takes its config in that slot.
              const response = await apiClient.delete(endpoint, {
                data: { twoFaCode: values.twoFaCode },
              });

              message.success(response.data?.message || `${userType} deleted successfully`);
              setDeleteModalVisible(false);
              deleteForm.resetFields();
              setUserData((current: any) => current ? { ...current, status: 'DELETED' } : current);
            } catch (error: any) {
              message.error(error.response?.data?.message || `Failed to delete ${userType.toLowerCase()}`);
            } finally {
              setDeletingUser(false);
            }
          }}
        >
          <Form.Item
            name="twoFaCode"
            label="Your Current Admin 2FA Code"
            rules={[
              { required: true, message: 'Please enter your current 2FA code' },
              { pattern: /^\d{6}$/, message: 'Please enter a valid 6-digit code' },
            ]}
          >
            <Input
              maxLength={6}
              placeholder="Enter your 6-digit 2FA code"
              prefix={<SafetyOutlined />}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button danger type="primary" htmlType="submit" loading={deletingUser}>
                Delete {userType}
              </Button>
              <Button onClick={() => {
                setDeleteModalVisible(false);
                deleteForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </PageContainer>
  );
};

export default UserDetails;

